import { useNavigate } from 'react-router-dom';
import type { BrowsableDocument } from './useDocumentBrowserStore';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateShort, formatFileSize, formatVersionLabel, getDocumentDisplayTitle, getSourceKindLabel } from './documentBrowserUtils';
import { pluginRegistry } from '@/plugins/pluginRegistry';

interface TimelineSection {
  key: string;
  label: string;
  description: string;
  documents: BrowsableDocument[];
}

const TIMELINE_SECTION_ORDER: Record<string, number> = {
  review: 10,
  completed: 20,
  processing: 30,
  problems: 40,
  other: 50,
};

function getTimelineSectionKey(status: string): keyof typeof TIMELINE_SECTION_ORDER {
  switch (status) {
    case 'AwaitingConfirmation':
      return 'review';
    case 'Completed':
    case 'CompletedWithoutEmbeddings':
      return 'completed';
    case 'Uploaded':
    case 'Processing':
    case 'Parsing':
    case 'Parsed':
    case 'Extracting':
      return 'processing';
    case 'Failed':
    case 'PartiallyFailed':
    case 'EmptyDocument':
    case 'Deleted':
      return 'problems';
    default:
      return 'other';
  }
}

function getTimelineSectionMeta(key: keyof typeof TIMELINE_SECTION_ORDER): Omit<TimelineSection, 'documents'> {
  switch (key) {
    case 'review':
      return {
        key,
        label: 'Review offen',
        description: 'Dokumente warten auf fachliche Bestaetigung oder Freigabe.',
      };
    case 'completed':
      return {
        key,
        label: 'Abgeschlossen',
        description: 'Dokumente mit abgeschlossenem Ingest oder finalem Viewer-Status.',
      };
    case 'processing':
      return {
        key,
        label: 'In Bearbeitung',
        description: 'Uploads und laufende Pipeline-Schritte aus dem aktuellen Datenbestand.',
      };
    case 'problems':
      return {
        key,
        label: 'Probleme',
        description: 'Dokumente mit Fehlern, Teilfehlern oder unvollstaendigem Abschluss.',
      };
    case 'other':
    default:
      return {
        key,
        label: 'Weitere Ereignisse',
        description: 'Dokumente mit Status ausserhalb der Standardgruppen.',
      };
  }
}

function getTimelineEventDate(document: BrowsableDocument): string {
  switch (getTimelineSectionKey(document.status)) {
    case 'review':
    case 'completed':
    case 'problems':
      return document.completedAt ?? document.createdAt;
    case 'processing':
    case 'other':
    default:
      return document.createdAt;
  }
}

function getTimelineEventLabel(document: BrowsableDocument): string {
  switch (getTimelineSectionKey(document.status)) {
    case 'review':
      return 'Review seit';
    case 'completed':
      return document.completedAt ? 'Abgeschlossen am' : 'Aktiv seit';
    case 'problems':
      return 'Problem seit';
    case 'processing':
      return document.status === 'Uploaded' ? 'Eingegangen am' : 'In Bearbeitung seit';
    case 'other':
    default:
      return 'Ereignis am';
  }
}

function compareTimelineDocuments(left: BrowsableDocument, right: BrowsableDocument) {
  return new Date(getTimelineEventDate(right)).getTime() - new Date(getTimelineEventDate(left)).getTime();
}

function getTimelineSections(documents: BrowsableDocument[]): TimelineSection[] {
  const sections = new Map<string, TimelineSection>();

  for (const document of documents) {
    const sectionKey = getTimelineSectionKey(document.status);
    const existing = sections.get(sectionKey);

    if (existing) {
      existing.documents.push(document);
      continue;
    }

    sections.set(sectionKey, {
      ...getTimelineSectionMeta(sectionKey),
      documents: [document],
    });
  }

  return Array.from(sections.values())
    .map((section) => ({
      ...section,
      documents: [...section.documents].sort(compareTimelineDocuments),
    }))
    .sort((left, right) => TIMELINE_SECTION_ORDER[left.key] - TIMELINE_SECTION_ORDER[right.key]);
}

interface DocumentTimelineProps {
  documents: BrowsableDocument[];
  onSelectDocument: (document: BrowsableDocument) => void;
}

export function DocumentTimeline({ documents, onSelectDocument }: DocumentTimelineProps) {
  const navigate = useNavigate();
  const chunkVisualizerEnabled = pluginRegistry.isPluginEnabled('chunk-visualizer');

  if (documents.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Keine Dokumente in diesem Workspace.
      </div>
    );
  }

  const sections = getTimelineSections(documents);

  return (
    <div className="flex flex-col gap-6">
      {sections.map((section) => (
        <section key={section.key} className="rounded-2xl border border-border/60 bg-card/60 p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-1">
            <h3 className="text-lg font-semibold text-foreground">{section.label}</h3>
            <p className="text-sm text-muted-foreground">{section.description}</p>
          </div>
          <div className="flex flex-col gap-2 pl-4 border-l-2 border-muted">
            {section.documents.map((doc) => {
              const eventLabel = getTimelineEventLabel(doc);
              const eventDate = getTimelineEventDate(doc);

              return (
                <div
                  key={doc.id}
                  className="-ml-[calc(1rem+1px)] rounded-xl border border-border/60 bg-card/80 px-4 py-4 shadow-sm transition-colors hover:bg-muted/20"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-muted-foreground shrink-0">{eventLabel} {formatDateShort(eventDate)}</span>
                        <DocumentStatusBadge status={doc.status} />
                        <Badge variant="outline">{getSourceKindLabel(doc.sourceKind)}</Badge>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{getDocumentDisplayTitle(doc)}</p>
                        {doc.displayTitle && <p className="text-xs text-muted-foreground">{doc.fileName}</p>}
                        <p className="text-sm text-muted-foreground">
                          {doc.sourceLabel ?? 'Manueller Upload'} · {formatVersionLabel(doc)} · {formatFileSize(doc.fileSizeBytes)}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button variant="secondary" size="sm" onClick={() => onSelectDocument(doc)}>
                        Details
                      </Button>
                      {chunkVisualizerEnabled && (
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/chunk-visualizer?doc=${doc.id}`)}>
                          Chunks
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
